/* eslint-disable */

import React, { useState, useEffect } from 'react'
import { Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography, Paper, MenuItem } from '@mui/material'
import { api } from '../requests/axios'
import { YarnFormData } from '../utils/yarnParsing'

export type { YarnFormData }

interface KnittingNeedle {
  id: number
  needle_type: string
  us_size: number
  mm_size: number
}

interface YarnFormDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  onBack?: () => void
  formData: YarnFormData
  onChange: (data: YarnFormData) => void
  showReadOnlyData?: boolean
}

export default function YarnFormDialog({ open, onClose, onSubmit, onBack, formData, onChange, showReadOnlyData = true }: YarnFormDialogProps) {
  const [knittingNeedles, setKnittingNeedles] = useState<KnittingNeedle[]>([])

  useEffect(() => {
    if (open) {
      fetchKnittingNeedles()
    }
  }, [open])

  useEffect(() => {
    // Auto-select needle if we have parsed needle size and needles are loaded
    if (knittingNeedles.length > 0 && formData.gauge_needle_mm && formData.gauge_needle_us && !formData.gauge_knitting_needle_id) {
      // Try to find matching needle by mm size first (more precise), then US size
      const matchingNeedle = knittingNeedles.find((needle) => 
        Math.abs(needle.mm_size - formData.gauge_needle_mm!) < 0.1 && 
        Math.abs(needle.us_size - formData.gauge_needle_us!) < 0.1
      ) || knittingNeedles.find((needle) => 
        Math.abs(needle.mm_size - formData.gauge_needle_mm!) < 0.1
      ) || knittingNeedles.find((needle) => 
        Math.abs(needle.us_size - formData.gauge_needle_us!) < 0.1
      )
      
      if (matchingNeedle) {
        onChange({ ...formData, gauge_knitting_needle_id: matchingNeedle.id })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knittingNeedles.length, formData.gauge_needle_mm, formData.gauge_needle_us, formData.gauge_knitting_needle_id])

  const fetchKnittingNeedles = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/knitting_needles.json?needle_type=straight`)
      setKnittingNeedles(response.data.knitting_needles || response.data || [])
    } catch (error) {
      console.error('Error fetching knitting needles:', error)
    }
  }

  const formatNeedleLabel = (needle: KnittingNeedle) => {
    const typeMap: { [key: string]: string } = {
      straight: 'Straight',
      dbpt: 'Double Pointed',
      cir_16: 'Circular 16"',
      cir_24: 'Circular 24"',
      cir_32: 'Circular 32"'
    }
    return `US ${needle.us_size} / ${needle.mm_size}mm - ${typeMap[needle.needle_type] || needle.needle_type}`
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Yarn Details</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            label="Brand"
            fullWidth
            value={formData.brand}
            onChange={(e) => onChange({ ...formData, brand: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            label="Name"
            fullWidth
            value={formData.name}
            onChange={(e) => onChange({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Length"
                type="number"
                fullWidth
                value={formData.length}
                onChange={(e) => onChange({ ...formData, length: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Length Unit"
                select
                fullWidth
                value={formData.length_unit}
                onChange={(e) => onChange({ ...formData, length_unit: e.target.value })}
                sx={{ mb: 2 }}
                SelectProps={{ native: true }}
              >
                <option value="m">m</option>
                <option value="yd">yd</option>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Weight"
                type="number"
                fullWidth
                value={formData.weight}
                onChange={(e) => onChange({ ...formData, weight: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Weight Unit"
                select
                fullWidth
                value={formData.weight_unit}
                onChange={(e) => onChange({ ...formData, weight_unit: e.target.value })}
                sx={{ mb: 2 }}
                SelectProps={{ native: true }}
              >
                <option value="g">g</option>
                <option value="oz">oz</option>
              </TextField>
            </Grid>
          </Grid>
          <TextField
            label="Material"
            fullWidth
            multiline
            rows={2}
            value={formData.material}
            onChange={(e) => onChange({ ...formData, material: e.target.value })}
            sx={{ mb: 2 }}
            required
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
            Gauge Information (Optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Stitches"
                type="number"
                fullWidth
                value={formData.gauge_sts || ''}
                onChange={(e) => onChange({ ...formData, gauge_sts: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="e.g., 20"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Rows"
                type="number"
                fullWidth
                value={formData.gauge_rows || ''}
                onChange={(e) => onChange({ ...formData, gauge_rows: e.target.value })}
                sx={{ mb: 2 }}
                placeholder="e.g., 28"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Needle Size"
                select
                fullWidth
                value={formData.gauge_knitting_needle_id || ''}
                onChange={(e) => onChange({ ...formData, gauge_knitting_needle_id: parseInt(e.target.value) || undefined })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>Select needle size...</em>
                </MenuItem>
                {knittingNeedles.map((needle) => (
                  <MenuItem key={needle.id} value={needle.id}>
                    {formatNeedleLabel(needle)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {onBack && (
          <Button onClick={onBack}>Back</Button>
        )}
        <Button onClick={onSubmit} variant="contained">Next: Add Colorway</Button>
      </DialogActions>
    </Dialog>
  )
}
